import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { CreditNoteModal } from "components/AddCreditNoteModal/AddCreditNoteModal";
import { OptionDropdown } from "components/icons/optionDropdown";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDeleteCreditNoteMutation } from "store/apis/creditNotes";
import { CreditNote } from "store/apis/creditNotes";
import { CREDIT_NOTE_STATUS } from "constants/creditNotes";

type Props = {
  creditNote: CreditNote;
  customerId: string;
  customerName: string;
};

export const CreditNoteActions = ({
  creditNote,
  customerId,
  customerName,
}: Props) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();

  const {
    isOpen: isEditModalOpen,
    open: openEditModal,
    close: closeEditModal,
  } = useModal();

  const deleteModal = useModal();

  const [deleteCreditNote, { isLoading }] = useDeleteCreditNoteMutation();

  const handleDelete = async () => {
    try {
      await deleteCreditNote({
        groupId: group!.uuid,
        entityId: entityId!,
        customerId,
        credit_debit_note_id: creditNote.uuid,
      }).unwrap();

      successToast({
        title: "Credit note deleted successfully",
        message: `For ${customerName}`,
      });
      deleteModal.close();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <span>
            <Button
              customType="ghost_icon"
              size="small"
              isLoading={isLoading}
              disabled={isLoading}
            >
              <OptionDropdown color="currentColor" />
            </Button>
          </span>
        </Dropdown.Trigger>

        <Dropdown.Content align="end">
          <Dropdown.Item
            onClick={(e) => {
              openEditModal();
            }}
            disabled={
              creditNote.status !== CREDIT_NOTE_STATUS.AVAILABLE || isLoading
            }
          >
            Edit
          </Dropdown.Item>

          <Dropdown.Item
            onClick={(e) => {
              deleteModal.open();
            }}
            type="danger"
            disabled={
              creditNote.status !== CREDIT_NOTE_STATUS.AVAILABLE || isLoading
            }
          >
            <span>Delete</span>
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Root>

      <CreditNoteModal
        isOpen={isEditModalOpen}
        close={closeEditModal}
        customerId={customerId}
        entityId={entityId!}
        creditNote={creditNote}
      />

      <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
        <Modal.Content size="small">
          <Modal.Header>
            <Modal.Title>Delete credit note</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-text-body-sm t-text-text-30">
            Are you sure you want to delete this credit note?
            <br />
            This action cannot be undone.
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button disabled={isLoading}>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="danger"
              onClick={handleDelete}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Delete
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};
